import { createSlice } from "@reduxjs/toolkit";
import { AppThunk } from "./store";
import { colorShows } from "./colors";
import gsap from "gsap";

export interface AppState {
  alexaInterface: boolean;
  loadingAssets: boolean;
  poolMode: boolean;
  spaMode: boolean;
  poolTemp: number;
  spaTemp: number;
  heaterOn: boolean;
  phLevel: number;
  orpLevel: number;
  spaJets: boolean;
  waterfall: boolean;
  bubblers: boolean;
  fireBowl: boolean;
  spillover: boolean;
  poolLights: boolean;
  poolLightsBrightness: number;
  poolLightsColor: string;
  poolLightsColorShow: string;
  spaLights: boolean;
  spaLightsBrightness: number;
  spaLightsColor: string;
  spaLightsColorShow: string;
  bubblerLights: boolean;
  bubblerLightsBrightness: number;
  bubblerLightsColor: string;
  bubblerLightsColorShow: string;
  waterfallLights: boolean;
  waterfallLightsBrightness: number;
  waterfallLightsColor: string;
  waterfallLightsColorShow: string;
  landscapeLights: boolean;
  editingLightBrightness:
    | "poolLights"
    | "spaLights"
    | "bubblerLights"
    | "waterfallLights"
    | "";
  editingLightColor:
    | "poolLights"
    | "spaLights"
    | "bubblerLights"
    | "waterfallLights"
    | "";
  editingCustomLightColor:
    | "poolLights"
    | "spaLights"
    | "bubblerLights"
    | "waterfallLights"
    | "";
}

const initialState: AppState = {
  alexaInterface: false,
  loadingAssets: true,
  poolMode: true,
  spaMode: false,
  poolTemp: 75,
  spaTemp: 102,
  heaterOn: true,
  phLevel: 7.5,
  orpLevel: 750,
  spaJets: true,
  waterfall: true,
  bubblers: true,
  fireBowl: false,
  spillover: false,
  poolLights: true,
  poolLightsBrightness: 100,
  poolLightsColor: "#7AD9ED",
  poolLightsColorShow: "",
  spaLights: true,
  spaLightsBrightness: 100,
  spaLightsColor: "#08B096",
  spaLightsColorShow: "",
  bubblerLights: true,
  bubblerLightsBrightness: 100,
  bubblerLightsColor: "#0000FF",
  bubblerLightsColorShow: "",
  waterfallLights: true,
  waterfallLightsBrightness: 100,
  waterfallLightsColor: "#EE00EE",
  waterfallLightsColorShow: "",
  landscapeLights: true,
  editingLightBrightness: "",
  editingLightColor: "",
  editingCustomLightColor: "",
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
// export const incrementAsync = createAsyncThunk(
//   'counter/fetchCount',
//   async (amount: number) => {
//     const response = await fetchCount(amount);
//     // The value we return becomes the `fulfilled` action payload
//     return response.data;
//   }
// );

const reducers = Object.assign(
  {},
  ...Object.keys(initialState).map((key) => {
    const type = typeof (initialState as any)[key];
    let retval: any = {};
    if (type === "boolean") {
      const toggleKey = `toggle${key.charAt(0).toUpperCase() + key.slice(1)}`;
      retval[toggleKey] = (state: any) => {
        state[key] = !state[key];
      };
    } else if (type === "number") {
      const incrementKey = `increment${
        key.charAt(0).toUpperCase() + key.slice(1)
      }`;
      retval[incrementKey] = (state: any) => {
        state[key] += 1;
      };
      const decrementKey = `decrement${
        key.charAt(0).toUpperCase() + key.slice(1)
      }`;
      retval[decrementKey] = (state: any) => {
        state[key] -= 1;
      };
    }
    const setKey = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
    retval[setKey] = (state: any, action: any) => {
      state[key] = action.payload;
    };
    return retval;
  }),
  {
    togglePoolMode: (state: any) => {
      state.poolMode = !state.poolMode;
      if (state.poolMode) {
        state.spaMode = false;
        state.spaJets = false;
      }
    },
  },
  {
    toggleSpaMode: (state: any) => {
      state.spaMode = !state.spaMode;
      if (state.spaMode) {
        state.poolMode = false;
      }
    },
  },
  {
    setPartyMode: (state: any, action: any) => {
      state.spaJets = action.payload;
      state.waterfall = action.payload;
      state.bubblers = action.payload;
      state.fireBowl = action.payload;
      state.spillover = action.payload;
      state.waterfallLights = action.payload;
      state.poolLights = action.payload;
      state.spaLights = action.payload;
      state.bubblerLights = action.payload;
      if (action.payload) {
        state.waterfallLightsBrightness = 100;
        state.poolLightsBrightness = 100;
        state.spaLightsBrightness = 100;
        state.bubblerLightsBrightness = 100;
      }
    },
  },
  {
    togglePartyMode: (state: any, action: any) => {
      const currentState =
        state.spaJets &&
        state.waterfall &&
        state.bubblers &&
        state.fireBowl &&
        state.spillover;
      state.spaJets = !currentState;
      state.waterfall = !currentState;
      state.bubblers = !currentState;
      state.fireBowl = !currentState;
      state.spillover = !currentState;
      state.waterfallLights = !currentState;
      state.poolLights = !currentState;
      state.spaLights = !currentState;
      state.bubblerLights = !currentState;
      if (!currentState) {
        state.waterfallLightsBrightness = 100;
        state.poolLightsBrightness = 100;
        state.spaLightsBrightness = 100;
        state.bubblerLightsBrightness = 100;
      }
    },
  }
);

export const appSlice = createSlice({
  name: "app",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: reducers,
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  // extraReducers: (builder) => {
  //   builder
  //     .addCase(incrementAsync.pending, (state) => {
  //       state.status = 'loading';
  //     })
  //     .addCase(incrementAsync.fulfilled, (state, action) => {
  //       state.status = 'idle';
  //       state.value += action.payload;
  //     })
  //     .addCase(incrementAsync.rejected, (state) => {
  //       state.status = 'failed';
  //     });
  // },
});

function rgba2hex(orig: string) {
  var rgb: any = orig
      .replace(/\s/g, "")
      .match(/^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?/i),
    alpha = ((rgb && rgb[4]) || "").trim(),
    hex = rgb
      ? (rgb[1] | (1 << 8)).toString(16).slice(1) +
        (rgb[2] | (1 << 8)).toString(16).slice(1) +
        (rgb[3] | (1 << 8)).toString(16).slice(1)
      : orig;

  return "#" + hex;
}

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
// export const selectCount = (state: RootState) => state.counter.value;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export const runLightColorShow =
  (
    lightName: "poolLights" | "spaLights" | "bubblerLights" | "waterfallLights"
  ): AppThunk =>
  (dispatch, getState) => {
    const currentShowId: string = (getState().app as any)[
      lightName + "ColorShow"
    ];
    const currentColor: string = (getState().app as any)[lightName + "Color"];
    if (!currentShowId) {
      return;
    }
    const currentShow = colorShows.find((show) => show.id === currentShowId);
    if (!currentShow) {
      return;
    }

    const currentColorIndex = currentShow.colors.findIndex(
      (c) => c.toLowerCase() === currentColor.toLowerCase()
    );
    if (currentColorIndex < 0) {
      dispatch(
        (
          appSlice.actions[
            "set" +
              lightName.charAt(0).toUpperCase() +
              lightName.slice(1) +
              "Color"
          ] as any
        )(currentShow.colors[0])
      );
      dispatch(runLightColorShow(lightName));
      return;
    }

    var colorState = { color: currentColor };
    const nextColorIndex =
      currentColorIndex < 0 ||
      currentColorIndex === currentShow.colors.length - 1
        ? 0
        : currentColorIndex + 1;
    const nextColor = currentShow.colors[nextColorIndex];
    gsap.to(colorState, {
      duration: currentShow.timing / 1000,
      color: nextColor,
      onUpdate: () => {
        const updateShowId: string = (getState().app as any)[
          lightName + "ColorShow"
        ];
        if (updateShowId !== currentShowId) {
          return;
        }
        let colorTransition = colorState.color;
        if (colorTransition.startsWith("rgba")) {
          colorTransition = rgba2hex(colorTransition);
        }
        dispatch(
          (
            appSlice.actions[
              "set" +
                lightName.charAt(0).toUpperCase() +
                lightName.slice(1) +
                "Color"
            ] as any
          )(colorTransition)
        );
      },
      onComplete: () => {
        const updateShowId: string = (getState().app as any)[
          lightName + "ColorShow"
        ];
        if (updateShowId !== currentShowId) {
          return;
        }
        dispatch(
          (
            appSlice.actions[
              "set" +
                lightName.charAt(0).toUpperCase() +
                lightName.slice(1) +
                "Color"
            ] as any
          )(nextColor)
        );
        dispatch(runLightColorShow(lightName));
      },
    });
  };

export default appSlice.reducer;
